import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { ChevronRight, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

function readMinutes(body) {
  const words = (body || '').trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

/**
 * Articles index — newsroom homepage. Public, indexable, lives at /articles.
 * The text content here is the primary AdSense compliance lever — substantive,
 * evergreen English text that Google can crawl on every request.
 */
export default function ArticlesIndex() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/articles?limit=50`);
        const data = await res.json();
        setArticles(Array.isArray(data) ? data : []);
      } catch {
        setArticles([]);
      }
      setLoading(false);
    })();
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen bg-rooam-black text-white">
      <SeoHead
        title="Rooam Sports Newsroom — Articles, Analysis & Recaps"
        description="Long-form articles on youth basketball streaming, AAU tournaments, college recruiting, athlete development, and the business of amateur sports. Updated weekly by the Rooam Sports Network team."
        path="/articles"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Rooam Sports Newsroom",
          "url": "https://rooamsports.network/articles",
          "publisher": {
            "@type": "Organization",
            "name": "Rooam Sports Network",
            "logo": { "@type": "ImageObject", "url": "https://rooamsports.network/rooam-logo.png" }
          }
        }}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <header className="mb-10 border-b border-zinc-800 pb-8">
          <p className="text-sm uppercase tracking-[0.2em] text-red-500 font-bold mb-3">Newsroom</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
            STORIES FROM THE COURTSIDE.
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Analysis, recaps, and behind-the-scenes reporting on the AAU and youth basketball world — written by the people building the platform.
          </p>
        </header>

        {loading ? (
          <p className="text-zinc-500 text-sm">Loading articles…</p>
        ) : articles.length === 0 ? (
          <p className="text-zinc-500 text-sm">No articles yet. Check back soon.</p>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link
                to={`/articles/${featured.slug}`}
                className="block group mb-12 rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900/50 hover:border-red-500/40 transition-colors"
                data-testid={`article-featured-${featured.slug}`}
              >
                {featured.cover_image && (
                  <div className="aspect-[2/1] bg-black overflow-hidden">
                    <img src={featured.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                  </div>
                )}
                <div className="p-6 sm:p-8">
                  {(featured.tags || []).slice(0, 1).map(t => (
                    <span key={t} className="inline-block text-[10px] uppercase tracking-wider font-bold text-red-400 mb-2">{t}</span>
                  ))}
                  <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight group-hover:text-red-400 transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {featured.title}
                  </h2>
                  {featured.subtitle && (
                    <p className="text-zinc-400 mt-2 text-base sm:text-lg leading-relaxed">{featured.subtitle}</p>
                  )}
                  <div className="flex items-center gap-3 mt-4 text-xs text-zinc-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(featured.published_at)}</span>
                    <span>·</span>
                    <span>{featured.author}</span>
                  </div>
                </div>
              </Link>
            )}

            {/* Rest */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {rest.map(a => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="block group rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-red-500/40 transition-colors p-5 sm:p-6"
                  data-testid={`article-card-${a.slug}`}
                >
                  {(a.tags || []).slice(0, 1).map(t => (
                    <span key={t} className="inline-block text-[10px] uppercase tracking-wider font-bold text-red-400 mb-2">{t}</span>
                  ))}
                  <h3 className="text-lg sm:text-xl font-black text-white leading-snug group-hover:text-red-400 transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {a.title}
                  </h3>
                  {a.subtitle && <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{a.subtitle}</p>}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[11px] text-zinc-500 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />{formatDate(a.published_at)}
                    </span>
                    <span className="text-[11px] text-red-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
